import DeleteIcon from "@assets/icons/delete.svg";
import DuplicateIcon from "@assets/icons/duplicate.svg";
import { useI18n } from "@core/i18n/i18n";
import { ProgramContent } from "@domain/program/program";
import { DayIndex } from "@domain/program/program-days";
import styled from "@emotion/styled";
import { MenuButton } from "@ui/components/common/menu-button";
import { Tooltip } from "@ui/components/common/tooltip";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { hideDuplicateSessionHalo } from "@ui/pages/program-builder/quickstart/duplicate-session-signal";
import { useEditorActions } from "@ui/pages/program-builder/hooks/use-editor-actions";
import { physioMeMobileBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { useState } from "react";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { onboardingManager } from "@core/service-container";

interface DayColumnMenuButtonProps {
	editedProgram: ProgramContent;
	dayIndex: DayIndex;
	showQuickstartHalo?: boolean; // When true, show the onboarding tooltip/halo for this column
}

export const DayColumnMenuButton: React.FC<DayColumnMenuButtonProps> = ({ editedProgram, dayIndex, showQuickstartHalo = false }) => {
	const { format } = useI18n();
	const { openDuplicateColumnModal, onClearSession } = useEditorActions(editedProgram);
	const [showDuplicateItemHalo, setShowDuplicateItemHalo] = useState(false);

	const duplicateGuide = format("programBuilder.quickstart.step.duplicateSession.guide02");

	return (
		<MenuButtonContainer>
			<StyledTooltip
				interactive
				visible={showQuickstartHalo}
				appendTo={() => document.body}
				zIndex={10000}
				popperOptions={{ strategy: "fixed" }}
				placement="right"
				content={
					<OnboardingPanel
						onClose={e => { hideDuplicateSessionHalo(); e.stopPropagation(); }}
						text={duplicateGuide}
					/>
				}
				onClickOutside={() => hideDuplicateSessionHalo()}
				onHidden={() => hideDuplicateSessionHalo()}
			>
				<span>
				<StyledMenuButton
					shouldDisplayHalo={showQuickstartHalo}
					onMenuClick={() => {
						void onboardingManager.passStepOnboarding(
							OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_01,
						);
						if (showQuickstartHalo) {
							setShowDuplicateItemHalo(true);
							hideDuplicateSessionHalo();
						}
					}}
			menuItems={[
				{
					icon: <DuplicateIcon />,
					text: format("programBuilder.dayTable.columnMenu.duplicate"),
					onClick: () => { setShowDuplicateItemHalo(false); openDuplicateColumnModal(dayIndex); },
					shouldDisplayHaloItem: showDuplicateItemHalo,
				},
				{
					icon: <DeleteIcon />,
					text: format("programBuilder.dayTable.columnMenu.clear"),
					onClick: () => { setShowDuplicateItemHalo(false); onClearSession(dayIndex); },
					destructive: true,
				},
			]}
			menuIconColor={colors.black}
			menuIconSize={24}
				/>
				</span>
			</StyledTooltip>
		</MenuButtonContainer>
		);
};

const StyledMenuButton = styled(MenuButton)`
`;

const MenuButtonContainer = styled.div`
	position: absolute;
	right: 0;
	top: 0;

	@media ${physioMeMobileBreakpoint} {
		position: unset;
	}
`;

const StyledTooltip = styled(Tooltip)`
	padding: 0;

	.tippy-arrow {
		color: ${colors.onboardingPrimary};
	}

	.tippy-content {
		padding: 0;
	}
`;